import type { Dictionary } from './dictionaryTypes';
import { EN_ADMIN_INFRASTRUCTURE, EN_ADMIN_WIREGUARD_PAGE } from './enAdminOperatorSurfaces';
import { SETTINGS_PHONE_CHANGE_EN } from './settingsPhoneChange';

export const EN: Dictionary = {
    localeLabel: 'English',
    languages: {
        ru: 'Russian',
        en: 'English',
        es: 'Spanish',
        fr: 'French',
        ar: 'Arabic',
        'zh-CN': 'Mandarin Chinese',
        'zh-HK': 'Cantonese Chinese',
    },
    common: {
        close: 'Close',
        done: 'Done',
        settings: 'Settings',
        preferences: 'Preferences',
        logout: 'Log out',
        loading: 'Loading…',
        copied: 'Copied!',
        backToDashboard: 'Back to dashboard',
        backToChats: 'Back to chats',
        backToRecordings: 'Back to recordings',
        login: 'Login',
        forInvestors: 'For investors',
        learnMore: 'Learn more',
        chats: 'Chats',
        email: 'Email',
        openCall: 'Open call',
        currentBrowser: 'Current browser',
        anotherDevice: 'Another device',
        thisBrowser: 'This browser',
        moreActions: 'More actions',
    },
    appError: {
        title: 'Something went wrong',
        description: 'The error was reported. Reload the page to try again.',
        retry: 'Retry',
    },
    offline: {
        eyebrow: 'Offline',
        title: 'Connection is unavailable',
        description: 'You can reopen cached Chattenger screens, but new messages, calls, mail, and sync need a connection.',
        openChats: 'Open chats',
    },
    compatibilityRoutes: {
        errorEyebrow: 'Error route',
        errorTitle: 'The shared error surface now renders through Next.js.',
        errorDescription: 'This keeps the route alive while the rest of the product surface moves off the retired SPA.',
        towerEyebrow: 'Compatibility route',
        towerTitle: 'Tower now resolves through the Next.js shell.',
        towerDescription:
            'The game handoff will stay available during the SPA-to-Next cutover. The route is reserved and no longer depends on the retired bundle.',
        backHome: 'Back to Chattenger',
        joinEyebrow: 'Join',
        joinInvalidTitle: 'Invite link is invalid',
        joinInvalidDescription: 'This join link is malformed. Open it in Chattenger again or ask for a fresh link.',
        openInChattenger: 'Open in Chattenger',
        continueOnWeb: 'Continue on web',
        joinDirectChatTitle: 'Direct chat could not be opened',
        joinDirectChatDescription: 'The user was resolved, but the browser chat could not be initialized right now. Try opening it in the app.',
        goToDashboard: 'Go to dashboard',
        joinChannelEyebrow: 'Channel join',
        joinGroupEyebrow: 'Group join',
        joinUnsupportedChannelDescription: 'Web resolved this channel, but it is not available in your current browser chat list yet.',
        joinUnsupportedGroupDescription: 'Web resolved this group, but it is not available in your current browser chat list yet.',
        joinUnsupportedChannelFallback: 'Open this channel in Chattenger to preview it or complete access or subscription flow.',
        joinUnsupportedGroupFallback: 'Open this group in Chattenger to preview it or complete access or subscription flow.',
        joinNotFoundTitle: 'Invite not found',
        joinNotFoundDescription: 'This invite could not be resolved on the current account. It may be invalid, private, or no longer available.',
    },
    theme: {
        system: 'System',
        light: 'Light',
        dark: 'Dark',
        label: 'Theme',
        hint: 'Theme stays local to this browser device.',
    },
    settings: {
        eyebrow: 'Profile & settings',
        title: 'Interface preferences',
        description: 'Language and theme stay with this browser, so the interface feels familiar every time you return.',
        appearanceTab: 'Appearance',
        devicesTab: 'Devices',
        languageTab: 'Language',
        securityTab: 'Security',
        appearanceTitle: 'Visual mode',
        appearanceCopy: 'Switch the palette instantly without forcing a reload. The web client should stay fast and stable on every screen size.',
        languageTitle: 'Interface language',
        languageCopy: 'Localization updates immediately across the key web surfaces in the current browser session.',
        securityTitle: 'Trusted access',
        securityCopy: 'Approve new devices, revoke stale browsers, and review historical mobile-registration invites from a trusted session.',
        profileTitle: 'Current profile',
        profileCopy: 'Settings for this user and this browser.',
        publicCopy: 'Public pages still persist theme and language on the current device, even before sign-in.',
        photoAction: 'Photo',
        photoActionAria: 'Choose profile photo',
        photoUploadFailed: 'Failed to upload photo.',
        navigationTitle: 'Sections',
        languageLabel: 'Language',
        languageHint: 'Russian, English, español, français, العربية, 简体中文, and 廣東話 are supported right now.',
        localeBadge: 'Language',
        themeBadge: 'Theme',
        accessibilityTitle: 'Accessibility',
        accessibilityCopy:
            'Turn the semantic accessibility layer fully off for this browser if you want the most neutral visual mode without ARIA or role contracts.',
        accessibilityLabel: 'Accessibility',
        accessibilityOn: 'On',
        accessibilityOff: 'Off',
        focusHighlightsTitle: 'Focus highlights',
        focusHighlightsCopy: 'Turn the visual focus rings off for this browser if they feel noisy during normal mouse or touch use.',
        focusHighlightsLabel: 'Focus highlights',
        focusHighlightsOn: 'On',
        focusHighlightsOff: 'Off',
        securityLoadFailed: 'Failed to load the security surface.',
        securityActionFailed: 'Failed to complete the security action.',
        securityDateUnavailable: 'Unavailable',
        currentDeviceCannotBeRevoked: 'This browser is your current trusted device and cannot be revoked from itself.',
        ...SETTINGS_PHONE_CHANGE_EN,
        devicesTitle: 'Logged-in devices',
        devicesCopy: 'See every signed-in phone, browser, and desktop session for this account. Online state updates from the authenticated socket.',
        devicesLoadFailed: 'Failed to load logged-in devices.',
        devicesActionFailed: 'Failed to update the device session.',
        devicesEmpty: 'No devices were found for this account yet.',
        devicesCurrent: 'Current device',
        devicesView: 'View',
        devicesEndSession: 'End session',
        devicesEndingSession: 'Ending…',
        devicesLoggedInAt: 'Signed in',
        devicesUpdatedAt: 'Last update',
        devicesActiveSockets: 'Active sockets',
        devicesUserAgent: 'User agent',
        devicesStatusOnline: 'Online',
        devicesStatusOffline: 'Offline',
        devicesStatusSignedOut: 'Signed out',
        deviceInspectionBack: 'Back to devices',
        deviceInspectionCopy: 'Review recent activity from this signed-in device. Expand an event for metadata.',
        deviceInspectionEmpty: 'No recent activity yet.',
        deviceActivityNoMetadata: 'No metadata',
        deviceViewRequest: 'Request live view',
        deviceViewRequestMicrophone: 'Mic only',
        deviceViewRequestCameraMic: 'Camera + mic',
        deviceViewPermissionSummary: 'Target permissions: mic {microphone}, camera {camera}.',
        deviceViewPermissionGranted: 'granted',
        deviceViewPermissionPrompt: 'will prompt',
        deviceViewPermissionDenied: 'blocked',
        deviceViewPermissionUnknown: 'unknown',
        deviceViewPermissionUnsupported: 'unsupported',
        deviceViewPermissionHint: 'Choose granted options to avoid a permission prompt on that device.',
        deviceViewIdle: 'No active view request.',
        deviceViewPending: 'Waiting for the other device to start camera and mic.',
        deviceViewStarting: 'Starting camera and mic.',
        deviceViewStreaming: 'Camera and mic are streaming.',
        deviceViewPermissionRequired: 'Camera or mic permission is required on that device.',
        deviceViewFailed: 'Live view failed.',
        deviceViewStopped: 'Live view stopped.',
        deviceViewStreamingChip: '{count} device(s) viewing camera and mic',
        deviceViewAccept: 'Approve',
        deviceViewDecline: 'Decline',
        deviceViewPromptTitle: 'Allow device view?',
        deviceViewPromptBody: 'Another signed-in device is asking to view this browser. Only approve this if you requested it.',
        deviceViewRespondFailed: 'Could not respond to the device view request.',
        deviceViewOverlayTitle: 'Live device view',
        deviceViewOverlayCopy: 'Use this pane to watch the view status and account-safe activity from the selected device.',
        deviceViewOverlayMediaPending: 'A live camera/mic stream will appear here when the other device starts streaming.',
        deviceViewOverlayAudioActive: 'Audio stream active',
        deviceViewOverlayLogsTitle: 'Activity stream',
        pendingApprovalsTitle: 'Pending device approvals',
        pendingApprovalsCopy: 'These sign-in attempts are waiting for approval from one of your trusted devices.',
        pendingApprovalsEmpty: 'No pending device approvals right now.',
        pendingApprovalsApprove: 'Approve',
        pendingApprovalsReject: 'Reject',
        pendingApprovalsKnownDevice: 'Known device',
        pendingApprovalsUnknownDevice: 'New device',
        trustedDevicesTitle: 'Trusted devices',
        trustedDevicesCopy: 'Review the browsers and clients that can sign in without waiting for a second approval.',
        trustedDevicesEmpty: 'No trusted devices were found for this account yet.',
        trustedDevicesCurrent: 'Current device',
        trustedDevicesRevoke: 'Revoke device',
        trustedDevicesTrustedAt: 'Trusted',
        trustedDevicesUpdatedAt: 'Updated',
        trustedDevicesStatusTrusted: 'Trusted',
        trustedDevicesStatusPending: 'Pending',
        trustedDevicesStatusRevoked: 'Revoked',
        invitesTitle: 'Mobile registration invites',
        invitesCopy: 'Review historical phone-bound invites. New accounts must be created in the Chattenger mobile app.',
        invitesEmpty: 'No active or historical invites yet.',
        invitesRevoke: 'Revoke invite',
        invitesExpiresAt: 'Expires',
        invitesConsumedAt: 'Consumed',
        invitesRevokedAt: 'Revoked',
        invitesStatusActive: 'Active',
        invitesStatusConsumed: 'Consumed',
        invitesStatusRevoked: 'Revoked',
    },
    landing: {
        eyebrow: 'Secure communications platform',
        title: 'Chattenger — limitless opportunities',
        description:
            'A secure communications ecosystem for alliances, states, companies, and teams. Secure communications, digital sovereignty, and the path from trusted messaging to larger economic infrastructure.',
        searchHint: 'Chattenger. Чаттенджер. Chatenjer.',
        openLogin: 'Open login',
        learnMore: 'Learn more',
        badges: ['Responsive first', 'Socket native', 'Investor ready'],
        capabilities: [
            'Chats, groups, channels, voice notes, circle videos, and encrypted direct flows.',
            'One smooth conversation experience across phone and browser.',
            'Admin, support, recordings, and browser-first product surfaces in one product.',
        ],
        investorsEyebrow: 'BRICS / SCO / B2B / B2C',
        investorsTitle:
            'Chattenger is a communications core that can extend into sovereign digital ecosystems, public services, finance, logistics, and industry-specific super-app surfaces.',
        investorsDescription:
            'Secure communications, digital sovereignty, extensible mini-app architecture, and the path from trusted messaging to larger economic infrastructure.',
        coreNow: 'Core now',
        platformDirection: 'Platform direction',
        coreList: [
            'Chats, calls, group rooms, media exchange, notifications, and recordings.',
            'Private messaging, protected calls, and one familiar experience across devices.',
            'Admin, support, and secure operator surfaces.',
        ],
        platformList: [
            'Mini-apps, KYC, payments, logistics, CRM, media, and public-sector surfaces.',
            'Alliance-scale and state-scale communications, not only a consumer messenger.',
            'A flexible platform for regional rollouts and industry-specific environments.',
        ],
    },
    auth: {
        title: 'Chattenger',
        loginTitle: 'Sign in with a code',
        loginDescription: 'Enter your number to sign in. QR is available too.',
        runtimeUnavailableTitle: 'Stand unavailable',
        runtimeUnavailableDescription: 'The server is not responding. Sign-in may fail while the stand recovers.',
        methodLabel: 'Login method',
        methodOtp: 'OTP',
        methodQr: 'QR',
        countryLabel: 'Country',
        phoneLabel: 'Phone',
        phonePlaceholder: '8 (900) 450-65-54',
        continue: 'Continue',
        requesting: 'Requesting…',
        otpLabel: 'OTP code',
        otpHint: 'Enter the verification code.',
        callOtpHint: 'Answer the call and enter the code you hear.',
        smsOtpHint: 'We’ll send you an SMS now. Enter the code.',
        verify: 'Verify',
        verifying: 'Verifying…',
        resendCode: 'Resend code',
        resendIn: 'Resend in {seconds}s',
        retryCall: 'Call again',
        retrySms: 'SMS again',
        switchToCall: 'Try call',
        switchToSms: 'Use SMS',
        changePhone: 'Change number',
        requestOtpFailed: 'Failed to request OTP',
        verifyOtpFailed: 'Failed to verify OTP',
        pendingDeviceApprovalTitle: 'Approve this sign-in',
        pendingDeviceApprovalDescription:
            'This account already exists. Finish the sign-in from one of your trusted devices before this browser gets access.',
        pendingDeviceApprovalApproveOnTrustedDevice: 'Open Chattenger on a trusted device and approve this sign-in request.',
        pendingDeviceApprovalCodeLabel: 'Approval code',
        pendingDeviceApprovalQrLabel: 'Device approval QR code',
        pendingDeviceApprovalWaiting: 'Waiting for approval…',
        pendingDeviceApprovalApproved: 'Approved. Finishing sign-in…',
        pendingDeviceApprovalExpiresIn: 'Expires in {seconds}s',
        pendingDeviceApprovalExpiredStatus: 'Expired',
        pendingDeviceApprovalRejectedStatus: 'Rejected',
        pendingDeviceApprovalNotFoundStatus: 'Unavailable',
        pendingDeviceApprovalExpired: 'This approval request expired. Start the sign-in again.',
        pendingDeviceApprovalRejected: 'This approval request was rejected from a trusted device.',
        pendingDeviceApprovalNotFound: 'This approval request is no longer available. Start again.',
        pendingDeviceApprovalPollFailed: 'Failed to refresh the approval status.',
        cantLogin: 'Can’t log in?',
        changedNumberRecovery: 'I changed my number',
        changedNumberRecoveryTitle: 'Changed your number?',
        changedNumberRecoveryDescription:
            'Enter the old account number, one recovery code, and the new number. We will verify the new phone before recovery continues.',
        changedNumberRecoveryNewPhoneLabel: 'New phone number',
        changedNumberRecoveryNewPhonePlaceholder: 'New number',
        changedNumberRecoveryRequestOtp: 'Send new-number code',
        changedNumberRecoveryComplete: 'Recover with new number',
        changedNumberRecoveryOtpSent: 'Code sent to the new number.',
        changedNumberRecoveryRequesting: 'Sending…',
        changedNumberRecoveryCompleting: 'Recovering…',
        registrationEmailTitle: 'Confirm your email',
        registrationEmailDescription: 'Enter the code we sent to your email before your account opens.',
        registrationEmailSentTo: 'We sent a 4-digit code to {email}.',
        trustedDeviceBootstrapEmailTitle: 'Confirm this trusted device',
        trustedDeviceBootstrapEmailDescription: 'Enter the email code to finish signing in on this device.',
        trustedDeviceBootstrapEmailSentTo: 'We sent a 4-digit code to {email}.',
        mobileRegistrationRequired: 'New accounts must be created in the Chattenger mobile app.',
        emailOtpLabel: 'Email code',
        emailOtpHint: 'Enter the 4-digit code from your email.',
        registrationRecoveryCodesTitle: 'Save your recovery codes',
        accountRecoveryTitle: 'Recover account',
        accountRecoveryDescription: 'Enter the phone code and one recovery code to reset trusted devices.',
        recoveryCodeLabel: 'Recovery code',
        recoveryCodePlaceholder: 'CHTG-XXXX-XXXX-XXXX-XXXX',
        recoveryCodeHint: 'Use one backup code you saved earlier.',
        accountRecoveryCodesTitle: 'New recovery codes',
        recoveryCodesDescription: 'These codes are shown once. Keep them private and offline.',
        recoveryCodesWriteDown: 'Write these codes down. They are the only self-service way to recover this account later.',
        copyRecoveryCodes: 'Copy codes',
        emailRecoveryCodes: 'Email codes',
        recoveryCodesCopied: 'Codes copied',
        recoveryCodesDeliveryTitle: 'Send recovery codes',
        recoveryCodesDeliveryDescription: 'Send a copy to {email}, or keep an unread copy in your Chattenger inbox after sign-in.',
        recoveryCodesVerifiedEmailLabel: 'your verified email',
        recoveryCodesInboxCopyTitle: 'Create an unread Chattenger inbox copy',
        recoveryCodesInboxCopyDescription: 'After sign-in, open Mail and keep the recovery-code email unread until you choose it.',
        sendRecoveryCodesEmail: 'Send to email',
        recoveryCodesEmailSending: 'Sending…',
        recoveryCodesInboxCopyCreating: 'Creating your Chattenger inbox copy…',
        recoveryCodesEmailSent: 'Email sent',
        recoveryCodesNextCountdown: 'Next ({seconds})',
        qrEyebrow: 'Chattenger Web',
        qrTitle: 'Sign in with QR',
        qrDescription: 'Open Chattenger on your phone and confirm the sign-in.',
        qrInstructionOne: 'Open Chattenger.',
        qrInstructionTwo: 'Go to Settings.',
        qrInstructionThree: 'Choose web sign-in and scan the code.',
        qrRefreshing: 'Refreshing code…',
        qrWaiting: 'Waiting for confirmation from your phone…',
        qrExpired: 'This code expired. Generate a new QR.',
        qrFailed: 'Failed to prepare QR login',
    },
    admin: {
        control: 'Control',
        role: 'Role',
        roleCopy: 'Administrative tools stay separate from the everyday product experience.',
        nav: {
            overview: 'Overview',
            messages: 'Messages',
            statistics: 'Statistics',
            logs: 'Logs',
            infrastructure: 'Infrastructure',
            wireguardInternal: 'Internal WireGuard',
            stands: 'Stands',
            builds: 'Builds',
            users: 'Users',
            permissions: 'Permissions',
            db: 'Database',
            docs: 'Docs',
            mermaid: 'Mermaid editor',
            reports: 'Reports',
            email: 'Email',
            mailboxes: 'Mailboxes',
            terminal: 'Terminal',
            authConfig: 'Auth config',
            features: 'Features',
        },
        surfaceCanonicalContractLabel: 'Canonical contract',
        docsWebPlanPageTitle: 'Web plan',
        docsShellEyebrow: 'Admin / Docs',
        docsShellDescription: 'Canonical planning and implementation reference for the current web surface.',
        mermaid: {
            canvasLabel: 'Diagram workspace',
            editorTab: 'Editor',
            errorLink: 'This diagram link is invalid or exceeds the safe limit.',
            errorSyntax: 'The diagram could not be rendered. Fix the Mermaid syntax and try again.',
            fit: 'Fit diagram',
            loading: 'Loading editor…',
            pageDescription: 'Edit a private diagram in the browser and share its bounded URL state without saving source to Chattenger.',
            pageEyebrow: 'Admin / Mermaid',
            pageTitle: 'Mermaid editor',
            previewTab: 'Preview',
            ready: 'Diagram is ready',
            rendering: 'Rendering diagram…',
            reset: 'Reset view',
            share: 'Copy share link',
            shareCopied: 'Share link copied',
            shareUnavailable: 'Copying the share link is unavailable in this browser.',
            sourceLabel: 'Mermaid source',
            zoomIn: 'Zoom in',
            zoomOut: 'Zoom out',
        },
        logsPage: {
            shellEyebrow: 'Admin / Logs',
            shellTitle: 'Observability logs',
            shellDescription: 'Query provider-backed operational logs across Grafana Loki and Rollbar without falling back to disk-only admin tails.',
            searchLabel: 'Search',
            searchPlaceholder: 'Search message, payload, or stack text',
            serviceLabel: 'Service',
            sourceLabel: 'Source',
            sinkLabel: 'Sink',
            environmentLabel: 'Environment',
            severityLabel: 'Severity',
            optionAnyService: 'Any service',
            optionAnySource: 'Any source',
            optionAnySink: 'Any sink',
            optionAnyEnvironment: 'Any environment',
            optionAnySeverity: 'Any severity',
            btnApply: 'Apply',
            btnApplyBusy: 'Refreshing…',
            resultsNoData: 'No log data',
            resultsSummaryOne: '{total} row · page {page}',
            resultsSummaryMany: '{total} rows · page {page}',
            filtersHintCopy: 'Filters are applied server-side with a short cache window so repeated admin refreshes do not hammer Loki or Rollbar.',
            btnPrev: 'Prev',
            btnNext: 'Next',
            tableColTime: 'Time',
            tableColService: 'Service',
            tableColSink: 'Sink',
            tableColSource: 'Source',
            tableColEnv: 'Env',
            tableColSeverity: 'Severity',
            tableColMessage: 'Message',
            tableColAction: 'Action',
            btnInspect: 'Inspect',
            emptyTitle: 'No rows',
            emptyCopy: 'Matching observability rows will appear here once readable Loki and Rollbar providers are configured for this environment.',
            errLoadLogs: 'Failed to load logs',
            errChangePage: 'Failed to change page',
            providerFallbackLabel: 'Grafana Loki',
            providerFallbackMessage: 'No provider status available yet.',
            providerOkCopy: 'Provider queried successfully for the current admin request.',
            providerInactiveCopy: 'Provider is not active for the current request.',
            sheetTitleFallback: 'Log details',
            sheetEyebrowPattern: '{service} / {sink}',
            sheetSummaryLabel: 'Summary',
            sheetTimeLine: 'Time: {time}',
            sheetEnvironmentLine: 'Environment: {env}',
            sheetOpenProviderLink: 'Open provider occurrence',
            sheetRawLabel: 'Raw',
            sheetPayloadLabel: 'Payload',
            valueEmDash: '—',
        },
        messagesPage: {
            shellEyebrow: 'Admin / Messages',
            shellTitle: 'Support conversations',
            shellDescription: 'Live CRM inbox for the seeded Chattenger support identity.',
            searchLabel: 'Search support chats',
            searchPlaceholder: 'Search by name, handle, phone',
            btnFilter: 'Filter',
            listEmptyTitle: 'No support chats',
            listEmptyCopy: 'Support conversations will appear here once regular users start writing to Chattenger.',
            noMessagesYet: 'No messages yet',
            lockedPill: 'Locked',
            pendingStreakPill: '{count} pending',
            threadEyebrow: 'Support thread',
            requesterMetaLine: '@{handle} · {count} pending user messages',
            openInNewTab: 'Open in new tab',
            btnUnlockInput: 'Unlock input',
            btnLockInput: 'Lock input',
            staffAttributionLine: '{name} (Chattenger)',
            fallbackStaffDisplayName: 'Chattenger',
            encryptedMessage: 'Encrypted message',
            composerPlaceholderCanReply: 'Reply as Chattenger',
            composerPlaceholderReadOnly: 'This staff account has read-only CRM access for support conversations.',
            btnReply: 'Reply',
            emptyDetailTitle: 'Select a support chat',
            emptyDetailHeading: 'The CRM detail panel opens here.',
            emptyDetailCopy: 'Message drilldown, live updates, lock controls, and acting-admin attribution all render in this pane.',
            errLoadThread: 'Failed to load the support thread',
            errSearchConversations: 'Failed to search support conversations',
            errOpenConversation: 'Failed to open this conversation',
            errSendReply: 'Failed to send the support reply',
            errUpdateLock: 'Failed to update the support-chat lock',
        },
        reportsPage: {
            shellEyebrow: 'Admin / Reports',
            shellTitle: 'Product reports',
            shellDescription: 'Incoming product reports with filters, drill-down, and operator review tools.',
            sheetEyebrowReportDetails: 'Report details',
            sheetTitleFallbackReport: 'Report',
            searchLabel: 'Search',
            searchPlaceholder: 'Search title, STR, ER, AR',
            userIdLabel: 'User id',
            userIdPlaceholder: 'Reporter id',
            statusLabel: 'Status',
            statusOptionAny: 'Any status',
            mediaLabel: 'Media',
            mediaOptionAny: 'Any media',
            mediaOptionVideo: 'Has video',
            mediaOptionImages: 'Has images',
            btnApply: 'Apply',
            btnPrev: 'Prev',
            btnNext: 'Next',
            resultsNoData: 'No data',
            resultsSummaryOne: '{total} report · page {page}/{totalPages}',
            resultsSummaryMany: '{total} reports · page {page}/{totalPages}',
            tableColCreated: 'Created',
            tableColStatus: 'Status',
            tableColTitle: 'Title',
            tableColUser: 'User',
            tableColMedia: 'Media',
            tableColAction: 'Action',
            mediaVideoTag: 'video',
            valueEmDash: '—',
            mediaImageCountOne: '{count} image',
            mediaImageCountMany: '{count} images',
            btnInspect: 'Inspect',
            emptyTitle: 'No reports',
            emptyCopy: 'Matching debug-bot reports will appear here once they are created.',
            errLoadReports: 'Failed to load reports',
            errChangePage: 'Failed to change page',
            sheetUserLine: 'User {userId}',
            sheetStrLabel: 'STR',
            sheetExpectedResultLabel: 'Expected result',
            sheetActualResultLabel: 'Actual result',
            sheetArtifactsLabel: 'Artifacts',
            sheetVideoKeyLine: 'Video key: {value}',
            sheetImageKeysLine: 'Image keys: {value}',
        },
        standaloneShellEyebrow: 'Admin',
        buildLastRunLabel: 'last run',
        buildEtaInProgressLabel: 'in progress',
        buildEtaLowConfidencePattern: '{phase} · low confidence',
        buildEtaNativeCompileLabel: 'native compile',
        buildHistoryArchiveLabel: 'Build archive',
        buildHistoryTitle: 'Build history',
        buildHistoryDescription: 'Terminal jobs are persisted in Postgres; full logs still stream from the builder filesystem on demand.',
        buildHistoryLoadingLabel: 'Loading history...',
        buildHistoryCountPattern: '{count} matching builds',
        buildHistoryStatusLabel: 'Status',
        buildHistorySearchLabel: 'Search',
        buildHistorySearchPlaceholder: 'branch, job, marker',
        buildHistoryApplyLabel: 'Apply',
        buildHistoryEmptyCopy: 'No matching build history yet.',
        buildHistoryPageStatusPattern: 'Page {page} of {pageCount} · {total} builds',
        buildHistoryCreatedPrefix: 'created',
        buildHistoryUploadedLabel: 'uploaded',
        buildHistoryAriaLabel: 'Admin build history',
        buildHistoryColumns: {
            artifact: 'Artifact',
            branch: 'Branch',
            duration: 'Duration',
            finished: 'Finished',
            job: 'Job',
            requestedBy: 'Requested by',
            status: 'Status',
            track: 'Track',
        },
        buildHistoryStatusOptions: {
            all: 'All',
            cancelled: 'Cancelled',
            failed: 'Failed',
            interrupted: 'Interrupted',
            queued: 'Queued',
            running: 'Running',
            succeeded: 'Succeeded',
            terminal: 'Terminal',
        },
        overviewPage: {
            shellTitle: 'Operations center',
            shellDescription: 'Messages, permissions, users, reports, logs, and staff tools for the protected Chattenger operations center.',
            heroCardLabel: 'Overview',
            heroCardTitle: 'Protected tools for operators, support, and moderation.',
            heroCardCopy: 'Keep live visibility across messages, users, permissions, and operational queues from one place.',
            ctaOpenMessages: 'Open Messages',
            ctaOpenStatistics: 'Open Statistics',
            ctaOpenLogs: 'Open Logs',
            ctaOpenDb: 'Open DB',
        },
        docsBrowserPage: {
            shellTitle: 'Working documents',
            shellDescription: 'Canonical plans, seed docs, and implementation references for operators, QA, and product reviews.',
            listPaneLabel: 'Documents',
            listPaneTitle: 'Canonical sources',
            listPaneCopy: 'Use these documents for QA behavior matrices, rollout planning, and the current product contract.',
            btnSyncSeedDocs: 'Sync seed docs to DB',
            btnSyncSeedDocsBusy: 'Syncing seed docs…',
            searchLabel: 'Search docs',
            searchPlaceholder: 'Search by title, slug, or summary',
            detailPaneLabel: 'Selected',
            btnCopyRawMarkdown: 'Copy raw markdown',
            copySuccessMessage: 'Raw markdown copied.',
            copyClipboardUnavailable: 'Clipboard access is unavailable in this browser.',
            copyFailedFallback: 'Failed to copy raw markdown.',
            errSyncSeedDocsFallback: 'Failed to sync seed docs.',
        },
        dbPage: {
            shellEyebrow: 'Admin / DB',
            shellTitle: 'PostgreSQL control room',
            shellDescription:
                'Browse live databases, inspect schemas and tables, run SQL, and create PostgreSQL roles through the protected backend admin surface.',
            labelActiveDatabase: 'Active database',
            labelSelectedSchema: 'Selected schema',
            labelSelectedTable: 'Selected table',
            btnRefreshExplorer: 'Refresh explorer',
            btnRefreshing: 'Refreshing…',
            btnSelectTop100: 'Select top 100',
            btnRunSql: 'Run SQL',
            btnRunning: 'Running…',
            transportBadge: 'Transport: PgBouncer via backend',
            baseAdminUserLine: 'Base admin user: {user}',
            unknownUser: 'unknown',
            resolvesAsPattern: '{name} resolves as {user}',
            pgbouncerNoticeLong:
                'PgBouncer currently runs in transaction mode. Each SQL execution should be treated as stateless across runs, but DDL and admin commands such as create role, create schema, and create database still work through this surface.',
            errLoadTableDetails: 'Failed to load table details',
            errRefreshTopology: 'Failed to refresh database topology',
            errRunQuery: 'Failed to run query',
            errCreateRole: 'Failed to create role',
            explorerLabel: 'Explorer',
            explorerTitle: 'Databases, schemas, tables',
            badgeDatabasesCount: '{count} databases',
            schemasCount: '{count} schemas',
            unavailable: 'Unavailable',
            schemaObjectsLine: '{tables} tables · {views} views',
            tableTypeView: 'View',
            tableTypeTable: 'Table',
            describeDbFallback: 'The backend could not describe this database through PgBouncer.',
            tableDetailLabel: 'Table detail',
            selectTablePrompt: 'Select a table',
            rowsApproxPattern: '~{count} rows',
            rowsUnknown: 'rows unknown',
            btnQueryThisTable: 'Query this table',
            btnCreateSchemaSnippet: 'Create schema snippet',
            btnCreateTableSnippet: 'Create table snippet',
            colOrdinal: '#',
            colColumn: 'Column',
            colType: 'Type',
            colNullable: 'Nullable',
            colDefault: 'Default',
            nullableYes: 'YES',
            nullableNo: 'NO',
            previewRowsLabel: 'Preview rows',
            previewRowsCopy: 'Showing up to {limit} rows from the backend preview.',
            previewEmptyTitle: 'No preview rows',
            previewEmptyCopy: 'The table is currently empty or the current PgBouncer mapping cannot read it.',
            indexesLabel: 'Indexes',
            indexesEmpty: 'No indexes were returned for this table.',
            noTableSelectedTitle: 'No table selected',
            noTableSelectedCopy: 'Choose any table from the explorer to inspect columns, indexes, and preview rows.',
            loadingTableDetails: 'Loading table details…',
            sqlConsoleLabel: 'SQL console',
            sqlConsoleTitle: 'Run direct PostgreSQL queries',
            badgeNoDatabase: 'No database',
            snippetSchema: 'Schema snippet',
            snippetTable: 'Table snippet',
            connectedAsPattern: 'Connected as {user}',
            resultCommandFallback: 'RESULT',
            resultRowsBadge: '{count} rows',
            truncatedBadge: 'Truncated to 200 rows',
            queryNoRowOutput: 'Query completed without row output.',
            queryNoResultSet: 'Command completed without a result set.',
            sqlEmptyStateCopy: 'Run SQL here to create schemas, tables, databases, or inspect live data directly.',
            rolesLabel: 'PostgreSQL roles',
            rolesTitle: 'Create database users',
            badgeRolesCount: '{count} roles',
            fieldRoleName: 'Role name',
            fieldRolePassword: 'Password',
            chkLogin: 'LOGIN',
            chkSuperuser: 'SUPERUSER',
            chkCreateDb: 'CREATEDB',
            chkCreateRole: 'CREATEROLE',
            roleMetaLogin: 'LOGIN',
            roleMetaNoLogin: 'NOLOGIN',
            roleMetaCreateDb: 'CREATEDB',
            roleMetaNoCreateDb: 'NOCREATEDB',
            roleMetaCreateRole: 'CREATEROLE',
            roleMetaNoCreateRole: 'NOCREATEROLE',
            roleBadgeSuperuser: 'SUPERUSER',
            roleBadgeRole: 'ROLE',
            btnCreateRole: 'Create role',
            btnCreatingRole: 'Creating…',
            roleCreatedLine: 'Role {role} created',
            valueEmDash: '—',
        },
        usersPage: {
            shellEyebrow: 'Admin / Users',
            shellTitle: 'Global role management',
            shellDescription: 'Global roles stay separate from ordinary chat-admin ownership. This surface controls only common/admin/super_admin.',
            searchLabel: 'Search',
            searchPlaceholder: 'Search by name or handle',
            btnSearch: 'Search',
            tableColUser: 'User',
            tableColHandle: 'Handle',
            tableColRole: 'Role',
            roleOptionCommon: 'Common',
            roleOptionAdmin: 'Admin',
            roleOptionSuperAdmin: 'Super admin',
            emptyTitle: 'No matching users',
            emptyCopy: 'Role-manageable users will appear here as soon as the query matches.',
            errSearchUsers: 'Failed to search users',
            errUpdateRole: 'Failed to update the user role',
        },
        statsPage: {
            shellEyebrow: 'Admin / Statistics',
            shellTitle: 'CRM activity and attribution',
            shellDescription: 'Filters, URL-synced state, and acting-admin attribution for support and role operations.',
            searchLabel: 'Search',
            searchPlaceholder: 'Search actor, target, path, text',
            eventLabel: 'Event',
            eventOptionAll: 'All events',
            btnApply: 'Apply',
            pageStatusPattern: 'Page {page} of {pageCount} · {total} rows',
            paginationNext: 'Next',
            paginationPrevious: 'Previous',
            tableColTime: 'Time',
            tableColEvent: 'Event',
            tableColActor: 'Actor',
            tableColTarget: 'Target',
            tableColPath: 'Path',
            tableColMessage: 'Message',
            actorSystemLabel: 'system',
            valueEmDash: '—',
            emptyTitle: 'No rows',
            emptyCopy: 'CRM activity rows will appear here as elevated users work with support and admin tools.',
            errLoadStats: 'Failed to load CRM statistics',
        },
        permissionsPage: {
            shellEyebrow: 'Admin / Permissions',
            shellTitle: 'CRM permission matrix',
            shellDescription:
                'This is the first extendable CRM permission surface. It controls admin access to support messaging and CRM statistics.',
            tableColPermission: 'Permission',
            tableColRole: 'Role',
            tableColAccessLevel: 'Access level',
            rowPermissionChatWithUsers: 'Chat with users',
            accessLevelFull: 'Full access',
            accessLevelRead: 'Read access',
            accessLevelNone: 'No access',
            roleUser: 'User',
            roleAdmin: 'Admin',
            roleSuperAdmin: 'Super admin',
            errUpdatePermission: 'Failed to update this permission',
        },
        authConfigPage: {
            shellEyebrow: 'Admin / Auth',
            shellTitle: 'Phone auth provider routing',
            shellDescription:
                'Postgres-backed country routing. Keep Exolve on RU/BY while viable; use Firebase phone for explicitly enabled international countries after billing, SMS region policy, and client config are proven. Env values are bootstrap fallback only.',
            sourceBootstrapEnv: 'Bootstrap env fallback',
            sourceDatabase: 'Postgres config',
            defaultSummary: 'Default country: {countryId}',
            searchLabel: 'Search countries',
            searchPlaceholder: 'Search by country, ISO code, or calling code',
            btnSave: 'Save routing',
            btnSaving: 'Saving...',
            btnReset: 'Reset',
            countriesPageStatusPattern: 'Page {page} of {pageCount} · {total} countries',
            errorsPageStatusPattern: 'Page {page} of {pageCount} · {total} failures',
            paginationNext: 'Next',
            paginationPrevious: 'Previous',
            tableColCountry: 'Country',
            tableColProvider: 'Provider',
            tableColStatus: 'Status',
            tableColDefault: 'Default',
            tableColUpdated: 'Updated',
            tableColErrorReason: 'Reason',
            providerExolve: 'Exolve call/SMS',
            providerFirebase: 'Firebase phone',
            providerUnsupported: 'Unsupported',
            statusEnabled: 'Enabled',
            statusDisabled: 'Disabled',
            defaultCurrent: 'Default',
            defaultSet: 'Set',
            dirtyCopy: 'Unsaved changes are local to this browser until you save routing.',
            defaultInvalid: 'Choose an enabled country with Exolve or Firebase as the default before saving.',
            emptyTitle: 'No countries',
            emptyCopy: 'No countries match the current search.',
            errorsTitle: 'Recent phone auth provider failures',
            errorsEmptyTitle: 'No recent failures',
            errorsEmptyCopy: 'Provider mismatch, disabled-country, Firebase, and Exolve delivery failures will appear here.',
            saveSuccess: 'Phone auth routing saved.',
            errLoad: 'Failed to load phone auth routing.',
            errSave: 'Failed to save phone auth routing.',
            valueEmDash: '—',
        },
        featuresPage: {
            shellEyebrow: 'Admin / Features',
            shellTitle: 'Feature flags',
            shellDescription:
                'Environment-local feature toggles backed by the backend control plane. Use this only for deliberate rollout decisions; provider secrets and raw env values stay server-side.',
            sourceDatabase: 'Postgres feature config',
            sourceCopy: 'This host maps to its current runtime environment automatically.',
            searchLabel: 'Search features',
            searchPlaceholder: 'Search by feature name',
            btnEnable: 'Enable',
            btnDisable: 'Disable',
            btnSave: 'Save',
            btnSaving: 'Saving...',
            btnReset: 'Reset',
            tableColFeature: 'Feature',
            tableColStatus: 'Status',
            tableColScope: 'Scope',
            tableColUpdated: 'Updated',
            tableColAction: 'Action',
            statusEnabled: 'Enabled',
            statusDisabled: 'Disabled',
            scopeServerSide: 'Server-side',
            scopeClientVisible: 'Client visible',
            scopeUnknown: 'Unknown',
            dirtyCopy: 'Unsaved feature changes are local to this browser until saved.',
            emptyTitle: 'No feature flags',
            emptyCopy: 'No feature flags match the current search.',
            saveSuccessPattern: '{featureName} saved.',
            errLoad: 'Failed to load feature flags.',
            errSave: 'Failed to save this feature flag.',
            valueEmDash: '—',
        },
        mailboxesPage: {
            shellEyebrow: 'Admin / Mailboxes',
            shellTitle: 'Mailbox accounts and aliases',
            shellDescription: 'Super-admin control plane for real mailbox logins, aliases, assignments, and environment-local console ownership.',
            runtimeSectionLabel: 'Runtime',
            runtimeHostSyncEnabledTitle: 'Environment-local mailbox host sync is enabled',
            runtimeHostSyncDisabledTitle: 'Mailbox host sync is disabled',
            runtimeMessageFallback: 'Mailbox runtime information is unavailable.',
            createFormEyebrow: 'Create mailbox account',
            createFormTitle: 'New account',
            btnCreateMailbox: 'Create mailbox',
            fieldSlug: 'Slug',
            fieldDisplayName: 'Display name',
            fieldLoginUser: 'Login user',
            fieldPassword: 'Password',
            fieldPasswordPlaceholderNewMailbox: 'Required for a new active mailbox',
            fieldPasswordHintKeepCurrent: 'Leave blank to keep the current password.',
            fieldFromAddress: 'From address',
            fieldFromName: 'From name',
            fieldReplyTo: 'Reply-To',
            fieldConsoleBinding: 'Console binding',
            consoleBindingNone: 'No console binding',
            consoleBindingEmail: '/admin/mail',
            fieldStatus: 'Status',
            statusPendingSetup: 'Pending setup',
            statusActive: 'Active',
            statusDisabled: 'Disabled',
            statusArchived: 'Archived',
            fieldSentMailbox: 'Sent mailbox',
            fieldTrashMailbox: 'Trash mailbox',
            fieldThreadScanLimit: 'Thread scan limit',
            fieldInboxPageSize: 'Inbox page size',
            aliasesSectionLabel: 'Aliases',
            btnAddAlias: 'Add alias',
            btnRemoveAlias: 'Remove alias',
            aliasFieldLabelPattern: 'Alias {index}',
            checkboxPrimaryAlias: 'Primary alias',
            checkboxEnabled: 'Enabled',
            assignedUsersLabel: 'Assigned Chattenger users',
            assignedUsersHintCreate: 'Common users may have only one mailbox. Admin and super-admin users may have multiple.',
            assignedUsersHintEdit: 'Regular users are limited to one mailbox account. Admin and super-admin users may hold multiple.',
            userOptionRoleLine: 'Role: {role}',
            forceAliasReassignCreate: 'Force alias reassign if an alias is already linked elsewhere',
            forceConsoleKeyReassignCreate: 'Force console binding move if the console is already linked elsewhere',
            forceAliasReassignEdit: 'Force alias reassign',
            forceConsoleKeyReassignEdit: 'Force console binding move',
            accountCardEyebrowPattern: 'Mailbox account #{id}',
            accountLoginUserLabel: 'Login user:',
            accountSystemMailboxSuffix: ' • system mailbox',
            btnSave: 'Save',
            btnArchive: 'Archive',
            archiveBlockedConsoleHint: 'Move the console binding away from this mailbox before archiving it.',
            archiveBlockedSystemHint: 'System mailbox accounts stay reserved and cannot be archived from this surface.',
            errCreateFallback: 'Failed to create the mailbox account',
            errSaveFallback: 'Failed to save the mailbox account',
            errArchiveFallback: 'Failed to archive the mailbox account',
            errConflictAliasLine: '{value} is already linked to {mailboxDisplayName}.',
            errConflictConsoleKeyLine: '{value} is already bound to {mailboxDisplayName}.',
            errConflictLoginLine: 'Login {value} is already used by {mailboxDisplayName}.',
            createSuccessLine: 'Mailbox {displayName} created.',
            saveSuccessLine: 'Mailbox {displayName} saved.',
        },
        ciPage: {
            shellTitle: 'CI',
            shellDescription:
                'Choose an environment, pin a branch, and deploy Next.js into the stable runtime roots without reusing the mutable working checkout.',
        },
        wireguardPage: EN_ADMIN_WIREGUARD_PAGE,
        terminalPage: {
            shellEyebrow: 'Admin / Terminal',
            shellTitle: 'Codex terminal',
            shellDescription:
                'Start and watch local Codex sessions from the admin console. This surface is scoped to Codex, not arbitrary shell access.',
            statusReady: 'Codex ready',
            statusMissingHome: 'Codex home missing',
            statusMissingBinary: 'Codex binary missing',
            sessionStatusRunning: 'Running',
            sessionStatusExited: 'Exited',
            sessionStatusFailed: 'Failed',
            knownSessionCached: 'Cached',
            knownSessionMissing: 'Missing',
            environmentLabel: 'Environment',
            prefixLabel: 'Session prefix',
            homePathLabel: 'Codex home',
            binaryPathLabel: 'Codex binary',
            searchedPathsLabel: 'Searched paths',
            activeSessionsLabel: 'Active sessions',
            knownSessionsLabel: 'Known Codex sessions',
            knownSessionSearchEmpty: 'No Codex sessions match this filter.',
            knownSessionSearchLabel: 'Filter known Codex sessions',
            knownSessionSearchPlaceholder: 'Filter sessions...',
            noSessionsTitle: 'No browser-managed Codex sessions yet.',
            noSessionsCopy: 'Create a session to start talking to Codex from this admin surface.',
            noKnownSessionsTitle: 'No cached Codex sessions yet.',
            noKnownSessionsCopy: 'Refresh the session index after Codex has been used on this host.',
            newSessionTitle: 'New Codex session',
            promptLabel: 'First prompt',
            promptPlaceholder: 'Ask Codex what to do on this host...',
            btnNewSession: 'New session',
            btnNewSessionBusy: 'Starting...',
            btnRefresh: 'Refresh',
            btnStop: 'Stop',
            btnSend: 'Send',
            btnSendBusy: 'Sending...',
            inputLabel: 'Message Codex',
            inputPlaceholder: 'Type a prompt, command, or instruction...',
            outputTitle: 'Terminal output',
            historyTitle: 'Codex history',
            emptyOutput: 'Output will appear here after Codex starts responding.',
            selectSessionTitle: 'No Codex session selected.',
            selectSessionCopy: 'Open a known session to load its verified scrollback, or start a new live browser session.',
            historyMissing: 'That Codex session is still in the cache, but its session log is no longer on disk.',
            historyLoading: 'Loading Codex history...',
            historyLoadingMore: 'Loading older Codex history...',
            unavailableTitle: 'This host is not ready for Codex admin sessions.',
            errLoad: 'Could not refresh terminal state.',
            errCreate: 'Could not start a Codex session.',
            errSend: 'Could not send input to Codex.',
            errStop: 'Could not stop the Codex session.',
            valueEmpty: 'Not available',
        },
        placeholderPage: {
            migrationCardLabel: 'Migration in progress',
        },
        mailConsole: {
            shellEyebrow: 'Admin / Mail',
            shellTitle: 'Support inbox',
            shellDescription: 'Support and operational mailbox for inbound customer and internal workflow email.',
            inboxPaneEyebrow: 'Inbox',
            inboxTitle: 'Support mail',
            inboxDescription: 'Use this surface for support and operational mail without leaving the Chattenger control center.',
            composeLabel: 'New email',
            composePlaceholder: 'Write a plain-text email',
            composeReplyHeading: 'Reply draft',
            composeForwardHeading: 'Forward draft',
            emptyInboxCopy: 'Inbox is empty right now.',
            emptySelectionCopy: 'Select an inbox row to inspect the message, or start a fresh outbound draft.',
            successPrefix: 'Email accepted for delivery',
            actionNewEmail: 'New email',
            actionReply: 'Reply',
            actionForward: 'Forward',
            actionDelete: 'Delete',
            sendEmail: 'Send email',
            addAttachments: 'Add attachments',
            refreshInbox: 'Refresh inbox',
            fieldTo: 'To',
            fieldCc: 'Cc',
            fieldBcc: 'Bcc',
            fieldSubject: 'Subject',
            fieldBody: 'Body',
            placeholderTo: 'user@example.com',
            placeholderCc: 'cc@example.com',
            placeholderBcc: 'bcc@example.com',
            placeholderSubject: 'Subject',
            attachmentsBadge: 'Attachments',
            attachmentRemoveAffix: ' • remove',
            previewUnavailable: 'No preview available',
            selectedMessageEyebrow: 'Selected message',
            selectedMessageEmptyTitle: 'No message selected',
            unknownDate: 'Unknown date',
            fromLabel: 'From',
            toLabel: 'To',
            ccLabel: 'Cc',
            messageIdLabel: 'Message-ID',
            deliveryLabel: 'Delivery',
            htmlOnlyFallback: 'HTML-only message. Raw HTML stays hidden by default here.',
            threadLabel: 'Thread',
            threadUnknownSubject: '(no subject)',
            threadDirectionSent: 'Sent',
            threadDirectionReceived: 'Received',
            errLoadMessage: 'Failed to load this email',
            errLoadAttachments: 'Failed to load attachments',
            errRefreshInbox: 'Failed to refresh the inbox',
            errOpenMessage: 'Failed to open this message',
            errDeleteMessage: 'Failed to delete this email',
            errSendEmail: 'Failed to send this email',
            successMovedToPattern: 'Message moved to {destination}',
            deliveryQueued: 'Queued',
            deliveryDeferred: 'Deferred',
            deliverySent: 'Sent',
            deliveryBounced: 'Bounced',
            deliveryExpired: 'Expired',
            deliveryUnknown: 'Unknown',
        },
        infrastructure: EN_ADMIN_INFRASTRUCTURE,
    },
    assistant: {
        navLabel: 'Assistant',
        eyebrow: 'Assistant',
        title: 'Ask Chattenger Assistant',
        description: 'Use this dedicated assistant surface for help. It never opens as a normal chat or searchable contact.',
        inputLabel: 'Prompt',
        inputPlaceholder: 'Ask for help with Chattenger, your workspace, or a deterministic web task.',
        submit: 'Ask',
        submitting: 'Thinking…',
        pendingLabel: 'Assistant is typing…',
        responseLabel: 'Response',
        unavailableTitle: 'Assistant unavailable',
        unavailableCopy: 'Assistant is not available right now. Try again later.',
        emptyCopy: 'Assistant responses depend on the backend provider being enabled.',
    },
    vpnPlusShareCode: {
        eyebrow: 'VPN+ share',
        title: 'Redeem shared VPN+ data',
        description: 'Sign in with your existing Chattenger account to move this held VPN+ allowance to your balance.',
        codeLabel: 'Share code ID',
        invalidCodeFallback: 'Invalid code',
        loginTitle: 'Sign in to continue',
        loginCopy: 'Sign in with an existing account to redeem this VPN+ allowance.',
        loginAction: 'Sign in',
        readyTitle: 'Ready to redeem',
        readyCopy: 'This code can be redeemed once. Chattenger will verify it before changing your VPN+ balance.',
        redeemAction: 'Redeem',
        redeemingAction: 'Redeeming…',
        redeemingTitle: 'Checking code',
        redeemingCopy: 'Chattenger is validating this VPN+ share code.',
        redeemedTitle: 'VPN+ data added',
        redeemedCopy: 'The shared VPN+ allowance was added to your account.',
        expiredTitle: 'Code expired',
        expiredCopy: 'This share code is no longer active. Ask the sender for a fresh one.',
        usedTitle: 'Code already used',
        usedCopy: 'This share code was already redeemed and cannot be used again.',
        cancelledTitle: 'Code cancelled',
        cancelledCopy: 'The sender cancelled this code before it was redeemed.',
        selfRedeemTitle: 'Choose another account',
        selfRedeemCopy: 'The sender cannot redeem their own VPN+ share code.',
        insufficientBalanceTitle: 'Allowance unavailable',
        insufficientBalanceCopy: 'This share code can no longer transfer its held VPN+ allowance.',
        rateLimitedTitle: 'Please wait',
        rateLimitedCopy: 'Try this share code again after the short wait.',
        invalidTitle: 'Code unavailable',
        invalidCopy: 'This share code is invalid or malformed.',
        featureOffTitle: 'VPN+ sharing is unavailable',
        featureOffCopy: 'VPN+ share-code redemption is not enabled for this account right now.',
        unavailableTitle: 'Could not redeem',
        unavailableCopy: 'Chattenger could not redeem this code right now. Try again later.',
        copyLink: 'Copy link',
        copyLinkSuccess: 'VPN+ share link copied.',
        copyLinkFailed: 'Could not copy this link.',
    },
    dashboard: {
        eyebrow: 'Dashboard',
        title: 'Fast, synced, and ready on every screen.',
        recordings: 'Recordings',
        searchChats: 'Search chats',
        searchChatsPlaceholder: 'Search chats',
        searchOlderChats: 'Searching older chats…',
        globalSearchLabel: 'Search Chattenger',
        globalSearchTrigger: 'Search',
        globalSearchPlaceholder: 'Search chats, people, and channels',
        searchBack: 'Back',
        searchEmptyChats: 'No chats found.',
        searchEmptyChannels: 'No channels found.',
        searchFilterAll: 'All',
        searchFilterChannels: 'Channels',
        searchFilters: 'Search filters',
        noChatsCompact: 'No chats yet :(',
        quickCreate: 'Quick create',
        create: 'Create',
        createChat: 'New chat',
        createChannel: 'New channel',
        createGroup: 'New group',
        closeSearch: 'Close search',
        closeQuickCreate: 'Close quick create menu',
        searchUsers: 'Search users',
        searchUsersPlaceholder: 'Find a user',
        searchUsersSearching: 'Searching users…',
        searchUsersEmpty: 'No users found',
        quickPeople: 'Quick people',
        notifications: 'Notifications',
        notificationsCopy: 'Allow notifications so messages and calls still reach you in the browser.',
        allowNotifications: 'Allow notifications',
        notificationReady: 'Notifications ready',
        refreshPush: 'Refresh push',
        disablePush: 'Disable push',
        realtimeLabel: 'Connection',
        realtimeOnline: 'Live',
        realtimeConnecting: 'Connecting…',
        realtimeReconnecting: 'Reconnecting…',
        realtimeOnlineCopy: 'Messages and events arrive right away.',
        realtimeConnectingCopy: 'The shell is ready while the connection comes up.',
        realtimeReconnectingCopy: 'Queued sends stay safe and continue after reconnect.',
        syncLabel: 'Sync',
        syncReady: 'Ready',
        syncRefreshing: 'Refreshing…',
        syncing: 'Syncing…',
        saving: 'Saving…',
        hidePreview: 'Hide message text in notification previews',
        showPreview: 'Show preview',
        vpn: 'VPN',
        vpnChecking: 'Checking VPN status…',
        channel: 'Channel',
        conversation: 'Conversation',
        attachments: 'Attachments',
        searchInChat: 'Search in chat',
        searchInChatPlaceholder: 'Search messages',
        searchingMessages: 'Searching…',
        noHits: 'No hits',
        jumpToLatest: 'Jump to latest',
        queuedSuffix: 'queued',
        shellCacheCopy: 'Recent chats and pending sends stay local, so the interface comes back instantly.',
        waitingThreadEyebrow: 'No chat selected',
        waitingThreadTitle: 'Pick a conversation from the left rail.',
        waitingThreadCopy: 'History, search, and the send queue will mount as soon as a chat is selected.',

        homeRecentLabel: 'Continue the conversation',

        noChatsEyebrow: 'No chats',
        noChatsCopy: 'Your chat list will appear here once sync is complete.',
        encryptedMessage: 'Encrypted message',
        encryptedThreadEmpty: 'All messages are encrypted',
        conversationPeerOnline: 'Online',
        conversationPeerLastSeenRecently: 'Last online recently',
        conversationPeerLastOnlineAtTime: 'Online at {time}',
        conversationPeerLastOnlineDateTime: 'Online {date} at {time}',
        messageMediaQueuedImage: 'Queued image',
        messageMediaQueuedVideo: 'Queued video',
        messageMediaVideoBadge: 'Video',
        messageMediaImageFallback: 'Image {index}',
        messageMediaVideoFallback: 'Video {index}',
        messageMediaThumbnailFallback: 'Preview',
        circleVideoExpandAria: 'Expand circle video',
        circleVideoResumeAria: 'Resume circle video',
        circleVideoCollapseAria: 'Collapse circle video',
        circleVideoPlaceholder: 'Circle video preview is loading',
        circleVideoPaused: 'Paused',
        circleVideoLivePreview: 'Live preview',
        circleVideoUnmute: 'Unmute',
        circleVideoMute: 'Mute',
        circleVideoSeekHandleAria: 'Seek circle video',
    },
    calls: {
        groupCall: 'Group call',
        directCall: 'Direct call',
        waitingMediaEyebrow: 'Waiting for media',
        waitingMediaTitle: 'The room is already live.',
        waitingMediaCopy: 'Turn on your mic, camera, or screen share to start the flow.',
        roomRecordings: 'Room recordings',
        recordingsSheetPrivateEyebrow: 'Private to you',
        viewRecordings: 'View recordings',
        callHistory: 'Calls',
        callHistoryTitle: 'Calls, recordings, and live activities',
        callHistoryCopy: 'Review recent rooms, open owner-private recordings, and continue call activities from one compact workspace.',
        callHistoryEmpty: 'Calls will appear here after you start or join a room.',
        callHistoryRefresh: 'Refresh',
        callHistoryRefreshing: 'Refreshing…',
        callHistoryRecordings: 'Recordings',
        callHistoryActivities: 'Activities',
        activities: 'Activities',
        activitiesMenu: 'Call activities',
        scrumPoker: 'Scrum poker',
        retroBoard: 'Retro board',
        startScrumPoker: 'Start poker',
        startRetroBoard: 'Start retro',
        activityLoadFailed: "Couldn't load call activities.",
        activityCreateFailed: "Couldn't create the activity.",
        activityUpdateFailed: "Couldn't update the activity.",
        noActivities: 'No activities in this room yet.',
        revealCards: 'Reveal cards',
        cardsRevealed: 'Cards revealed',
        pokerVotePrompt: 'Pick your card',
        pokerWaitingReveal: 'Votes stay hidden until a revealer opens the cards.',
        pokerAverage: 'Average {value}',
        retroBoardAddNote: 'Note',
        retroBoardAddText: 'Text',
        retroBoardAddRectangle: 'Rectangle',
        retroBoardAddEllipse: 'Ellipse',
        retroBoardSave: 'Save board',
        retroBoardViewOnly: 'View-only',
        retroBoardMoveHint: 'Drag the board to pan, use the wheel or controls to zoom, and drag items to arrange the retro.',
        retroBoardZoomIn: 'Zoom in',
        retroBoardZoomOut: 'Zoom out',
        retroBoardResetView: 'Reset view',
        incomingEyebrow: 'Incoming call',
        incomingCopy: 'Waiting for your answer in this browser',
        incomingBusyCopy: 'Finish the current call before switching rooms',
        incomingBusyLabel: 'Another call is active',
        answer: 'Answer',
        decline: 'Decline',
        leave: 'Leave',
        microphoneLabel: 'Microphone',
        videoLabel: 'Camera',
        microphoneOn: 'Mic on',
        microphoneOff: 'Mic off',
        cameraOn: 'Camera on',
        cameraOff: 'Camera off',
        shareScreen: 'Share screen',
        stopShare: 'Stop share',
        hideSelf: 'Hide self',
        showSelf: 'Show self',
        audioDevices: 'Audio devices',
        videoDevices: 'Video devices',
        microphoneDevice: 'Microphone',
        participantVolume: 'Participant volume',
        speakerDevice: 'Speaker',
        cameraDevice: 'Camera',
        backgroundEffects: 'Background',
        backgroundOff: 'None',
        backgroundBlur: 'Blur',
        backgroundLogo: 'Pigeon',
        backgroundUnavailable: 'Background effects need a supported browser and a strong enough device.',
        backgroundUsesSelectedOutput: 'The selected background is applied to the camera feed seen by remote participants and call recordings.',
        sameAsSystem: 'Same as system',
        audioProcessing: 'Audio processing',
        micGain: 'Microphone volume',
        micGainHint: 'Applies to your live microphone and to call recordings.',
        noiseSuppression: 'Noise reduction',
        echoCancellation: 'Echo cancellation',
        autoGainControl: 'Automatic gain control',
        audioOutputUnavailable: 'Speaker selection is not available in this browser. System output will be used.',
        deviceChangesApplyCopy: 'Microphone, speaker, and camera changes apply live without rejoining the room.',
        recordingUsesSelectedDevices:
            'Ongoing call recordings follow the selected microphone, camera, microphone volume, and audio processing settings. Speaker output only affects playback on this device.',
        callEndedUnexpectedly: 'The call ended unexpectedly.',
        callParticipantUnavailable: 'The call could not connect because nobody was available.',
        callConnectionUnavailable: 'Calls are temporarily unavailable. Please try again in a minute.',
        callParticipantBusy: 'The participant is already on another call.',
        callAnsweredElsewhere: 'This call was answered on another device.',
        layoutPreviewEyebrow: 'Layout preview',
        layoutPreviewCopy: 'This preview stays local to this browser. No real call or recording starts.',
        switchCamera: 'Switch camera',
        callMenu: 'Call menu',
        devicesAndRecording: 'Devices & recording',
        videoChatParticipants: 'Video chat participants',
        screenSharingMenu: 'Screen sharing',
        participantsOnCall: '{count} participants on call',
        screenShareActive: 'Screen share live',
        screenSharingParticipantLabel: 'Sharing: {name}',
        screenShareEmpty: 'No one is sharing a screen right now.',
        backToMenu: 'Back',
        fullscreenPreview: 'Live preview',
        ongoingCallLabel: 'Ongoing call',
        ongoingCallParticipants: '{count} people live',
        expand: 'Expand',
        minimizeCall: 'Minimize call',
        backToAutomaticLayout: 'Back to automatic layout',
        liveNow: 'Live now',
        mutedLabel: 'Muted',
        recordingStart: 'Start recording',
        recordingPause: 'Pause recording',
        recordingResume: 'Resume recording',
        screenShareUnsupported: 'Screen sharing is not available in this browser.',
        screenSharePermissionHint: 'Allow screen capture to share your screen or tab.',
        screenShareCanceled: 'Screen sharing was canceled.',
        screenShareStartFailed: "Couldn't start screen sharing.",
        compactActionMore: 'More',
        compactActionMicrophone: 'Mic',
        compactActionCamera: 'Camera',
        compactActionRecord: 'Record',
        compactActionPause: 'Pause',
        compactActionResume: 'Resume',
        compactActionSwitchCamera: 'Flip',
        compactActionLeave: 'Leave',
        directPreviewSwap: 'Swap preview',
        directPreviewHide: 'Hide preview',
        directPreviewRestore: 'Restore preview',
    },
    assetViewer: {
        dialogLabel: 'Attachment viewer',
        thumbnailsLabel: 'Items',
        open: 'Open',
        download: 'Download',
        downloadCurrent: 'Only this item',
        downloadAll: 'All items',
        downloadChoiceTitle: 'What do you want to download?',
        downloadChoiceDescription: 'Save just the current item or start downloads for every attachment in this message.',
        previous: 'Previous item',
        next: 'Next item',
        videoBadge: 'Video',
        pdfBadge: 'PDF',
        fileBadge: 'File',
    },
    mail: {
        attachments: 'Attachments',
        files: 'Files',
        shellTitle: 'Mailbox',
        workspaceNavigationLabel: 'Workspace navigation',
        workspaceTitle: 'Mailbox',
        mailboxLabel: 'Mailbox',
        actionNewEmail: 'New email',
        actionRefreshMailbox: 'Refresh',
        actionDeleteMailbox: 'Delete mailbox',
        threadsLabel: 'Threads',
        searchThreadsLabel: 'Search threads',
        filterAllThreads: 'All',
        filterIncomingThreads: 'Incoming',
        filterAttachmentThreads: 'Attachments',
        inboxQuietCopy: 'Threads will appear here once this mailbox receives or sends mail.',
        inboxQuietEyebrow: 'No threads yet',
        inboxQuietSearchCopy: 'No threads match this search right now.',
        inboxQuietTitle: 'Inbox is quiet',
        incoming: 'Incoming',
        noMailboxDefaultCopy: 'Your default mailbox will be',
        noMailboxEyebrow: 'Email',
        noMailboxSidebarCopy: 'Create your first mailbox to start sending and receiving email.',
        noMailboxSidebarTitle: 'No mailbox yet',
        noMailboxTitle: 'You don’t have an email mailbox yet',
        sent: 'Sent',
        productDraftNavEyebrow: 'Unsaved draft',
        productDraftNavTitle: 'Leave this draft?',
        productDraftNavCopy: 'Your draft is saved locally, but this navigation will leave the current compose view.',
        productDraftNavStay: 'Stay here',
        productDraftNavLeave: 'Leave draft',
        productMailboxDeleteEyebrow: 'Mailbox lifecycle',
        productMailboxDeleteTitle: 'Delete mailbox?',
        productMailboxDeleteCopyGeneric: 'Delete this mailbox from the regular product surface. You can restore it later.',
        productMailboxDeleteCopyWithAddress: 'Delete {address} from the regular product surface. You can restore it later from the empty state.',
        productMailboxDeleteKeep: 'Keep mailbox',
        productMailboxDeleteConfirm: 'Delete mailbox',
        productMailboxDeleteBusy: 'Deleting…',
        composeLabel: 'Compose',
        composeReplyHeading: 'Reply',
        composeForwardHeading: 'Forward',
        composeSendingAsPattern: 'Sending as {address}',
        actionCancel: 'Cancel',
        messageLabel: 'Message',
        threadLabel: 'Thread',
        noSubject: '(no subject)',
        threadMessageCountOne: '{count} message',
        threadMessageCountMany: '{count} messages',
        actionReply: 'Reply',
        actionForward: 'Forward',
        actionDeleteMessage: 'Delete',
        unknownSender: 'Unknown sender',
        unknownDate: 'Unknown date',
        fromLabel: 'From',
        toLabel: 'To',
        ccLabel: 'Cc',
        dateLabel: 'Date',
        deliveryLabel: 'Delivery',
        emptyThreadTitle: 'Pick a thread from the left rail',
        emptyThreadCopy: 'Open a conversation or start a fresh outbound email.',
        errRefreshMailbox: 'Failed to refresh this mailbox',
        errOpenMailbox: 'Failed to open this mailbox',
        errOpenThread: 'Failed to open this thread',
        actionCreateMailbox: 'Create mailbox',
        actionRestoreMailbox: 'Restore mailbox',
        successMailboxCreated: 'Mailbox created',
        errCreateMailbox: 'Failed to create this mailbox',
        successMailboxRestored: 'Mailbox restored',
        errRestoreMailbox: 'Failed to restore this mailbox',
        successMailboxDeleted: 'Mailbox deleted',
        errDeleteMailbox: 'Failed to delete this mailbox',
        errStageAttachments: 'Failed to stage attachments',
        successEmailAccepted: 'Email accepted for delivery',
        errSendEmail: 'Failed to send this email',
        successMessageMovedToTrash: 'Message moved to Trash',
        errDeleteMessage: 'Failed to delete this message',
        composerAddAttachments: 'Add attachments',
        composerSend: 'Send',
        composerSending: 'Sending…',
        composerHint: 'Drag files here, paste screenshots, or press Cmd/Ctrl+Enter to send.',
        composerRemoveAttachmentAria: 'Remove {fileName}',
    },
    recordings: {
        eyebrow: 'Recordings',
        title: 'Your owner-private room recording sessions across devices.',
        listTitle: 'Recordings',
        noRecordingsEyebrow: 'No recordings yet',
        noRecordingsTitle: 'Your recording sessions will appear here.',
        noRecordingsCopy: 'Start a group-call recording on web or mobile, and each private recording session will appear here separately.',
        detailEyebrow: 'Recording detail',
        playback: 'Playback',
        downloadMp4: 'Download MP4',
        sceneManifest: 'Scene manifest',
        newBadge: 'New',
        playbackPending: 'This recording is still waiting for call end or final packaging before playback is available.',
        playbackFailed: 'This recording failed during artifact preparation.',
        previewLabel: 'Preview',
        roomLabel: 'Room #{roomId}',
        chatFallbackLabel: 'Chat {chatId}',
        createdLabel: 'Created',
        roomStateLabel: 'Room state',
        roomStateEnded: 'Call ended',
        roomStateRecordingLive: 'Recording live',
        roomStateNoActive: 'No active recording',
        roomActivityLabel: 'Room activity',
        roomActivityCount: 'Active sessions: {count}',
        roomActivityIdle: 'Idle',
        artifactLabel: 'Artifact',
        durationLabel: 'Duration',
        durationSeconds: '{seconds}s',
        playbackReadyLabel: 'Ready to open',
        playbackPendingLabel: 'Not ready yet',
        details: 'Details',
        searchRecordingsLabel: 'Search recordings',
        filterAll: 'All',
        filterReady: 'Ready',
        filterActive: 'Active',
        sortRecent: 'Recent',
        sortChat: 'Chat',
        sortStatus: 'Status',
        paginationPrevious: 'Previous',
        paginationNext: 'Next',
        paginationStatus: 'Page {current} of {total}',
        lastStartedLabel: 'Last started',
        pausedAtLabel: 'Paused at',
        pendingRoomEndLabel: 'Pending room end',
        sealedAtLabel: 'Sealed at',
        artifactReadyLabel: 'Artifact ready',
        artifactFailedLabel: 'Artifact failed',
        sessionIdLabel: 'Session id',
        ownerDeviceLabel: 'Owner device',
        ownerOtherDeviceLabel: 'Another device #{deviceId}',
        artifactStatusLabel: 'Artifact status',
        artifactDurationLabel: 'Artifact duration',
        artifactSizeLabel: 'Artifact size',
        sizeMegabytes: '{size} MB',
        artifactErrorLabel: 'Artifact error',
        recordedIntervalsLabel: 'Recorded intervals',
        timeRangePattern: '{start} → {end}',
        ownerPrivateRecordingTitle: 'Owner-private recording',
        copiedLabel: 'Copied',
        copyFailedLabel: 'Copy failed',
        copyDetailsLabel: 'Copy details',
        playPlaybackLabel: 'Play',
        pausePlaybackLabel: 'Pause',
        seekPlaybackLabel: 'Seek playback',
        rewindTenSecondsLabel: 'Back 10 seconds',
        forwardTenSecondsLabel: 'Forward 10 seconds',
        enterFullscreenLabel: 'Open fullscreen',
        exitFullscreenLabel: 'Exit fullscreen',
        processingTimelineLabel: 'Processing timeline',
        summaryRecordingLine: 'Recording #{id}',
        summaryRoomLine: 'Room #{roomId}',
        summaryStatusLine: 'Status: {status}',
        summaryArtifactLine: 'Artifact: {artifact}',
    },
    uiKit: {
        shellEyebrow: 'Admin / UI kit',
        title: 'Web UI kit',
        description: 'Shared interface primitives used across Chattenger login, dashboard, calls, and admin surfaces.',
        buttonsAndBadges: 'Buttons and badges',
        inputsAndSelects: 'Inputs and selects',
        structuredInput: 'Structured input',
        overlayTitle: 'Sheet',
        overlayCopy: 'Mobile-first overlays reuse the same shell treatment as the chat attachments explorer.',
        openShowcaseSheet: 'Open showcase sheet',
        showcaseSheetEyebrow: 'UI kit',
        showcaseSheetTitle: 'Showcase sheet',
        showcaseSheetCopy: 'The sheet primitive is the shared base for mobile-first overlays in the new web client.',
        actionsCardLabel: 'Actions',
        demoButtonPrimary: 'Primary',
        demoButtonSecondary: 'Secondary',
        demoButtonGhost: 'Ghost',
        demoButtonCompact: 'Compact',
        badgeDefaultDemo: 'Default',
        badgeAccentDemo: 'Accent',
        badgeMutedDemo: 'Muted',
        badgeDangerDemo: 'Danger',
        selectionCardLabel: 'Selection',
        roleSelectLabel: 'Role',
        resourcesSelectLabel: 'Resources',
        resourcesHint: 'Representative CRM resource selection.',
        resourceOptionMessages: 'Messages',
        resourceOptionStats: 'Statistics',
        resourceOptionPermissions: 'Permissions',
        resourceOptionUsers: 'Users',
        structuredCardLabel: 'Structured input',
        arrayInputLabel: 'Notification emails',
        arrayInputAddLabel: 'Add email',
        overlaysCardLabel: 'Overlays',
        tabsShowcaseLabel: 'UI kit showcase',
        catalogNavigationLabel: 'UI kit catalog sections',
        catalogControlsTab: 'Controls',
        catalogFeedbackTab: 'Feedback and overlays',
        catalogDataTab: 'Data and media',
        catalogTextFieldLabel: 'Notification address',
        catalogTextFieldHint: 'Normal, disabled, and validation states are rendered together.',
        catalogTextFieldError: 'Use a complete address.',
        catalogOtpLabel: 'One-time code',
        catalogSliderLabel: 'Preview value',
        catalogArrayPlaceholder: 'Enter a notification address',
        catalogRemoveValueLabel: 'Remove value',
        catalogEmptySelection: 'Nothing selected',
        catalogSelectAction: 'Select',
        catalogSelectedAction: 'Selected',
        catalogLoadingLabel: 'Loading component preview',
        catalogSettingsLabel: 'Catalog settings',
        catalogToastAction: 'Show toast',
        catalogToastMessage: 'The UI kit preview action completed.',
        catalogTableLabel: 'Component state table',
        catalogTableColumnComponent: 'Component',
        catalogTableColumnState: 'State',
        catalogTableValueReady: 'Ready',
        catalogEmptyStateTitle: 'No sample records',
        catalogEmptyStateCopy: 'No sample records match this catalog state.',
        catalogShowEmptyState: 'Show empty state',
        catalogShowPopulatedState: 'Show populated state',
        catalogQrTitle: 'Catalog fixture QR code',
        catalogMediaAction: 'Open media preview',
        catalogMediaLabel: 'Catalog attachment preview',
        catalogMediaMeta: 'Safe local catalog fixture',
        roleDemoUser: 'Common user',
        roleDemoAdmin: 'Admin',
        roleDemoSuperAdmin: 'Super admin',
    },
};
